import pickBy from 'lodash/pickBy';
import map from 'lodash/map';

const toQueryString = (obj: $TSFixMe) => {
  const truthy_params = pickBy({
    ...obj,
  });
  const params = map(truthy_params, (v, k) => `${k}=${v}`);
  return `${params.length ? '?' : ''}${params.join('&')}`;
};

export const RootRoutes = Object.freeze({
  home: () => `/`,
  login: ({ type }: { type?: string } = {}) =>
    `/login${toQueryString({ type })}`,
  loginPasswordReset: () => `/login/password-reset`,
});

export const ClaimRoutes = Object.freeze({
  new: ({ account_id }: { account_id?: string } = {}) =>
    `/claims/new${toQueryString({ account_id })}`,
  list: () => `/claims`,
  detailEmployerSponsored: ({ claim_id }: { claim_id: string }) =>
    `/claims/employer-sponsored/${claim_id}`,
  detailPretaxClaim: ({ claim_id }: { claim_id: string }) =>
    `/claims/pretax-claim/${claim_id}`,
  detailPretaxTransaction: ({
    transaction_id,
    settlement_date,
    seq_number,
  }: $TSFixMe) =>
    `/claims/pretax-transaction/${transaction_id}${toQueryString({
      settlement_date,
      seq_number,
    })}`,
  success: ({ account, is_pretax, is_hsa }: $TSFixMe = {}) =>
    `/claims/success${toQueryString({ account, is_pretax, is_hsa })}`,
});

export const AccountRoutes = Object.freeze({
  list: () => `/accounts`,
  info: () => `/accounts/more-info`,
  detailEmployerSponsored: ({
    employee_wallet_id,
  }: {
    employee_wallet_id: string;
  }) => `/accounts/employer-sponsored/${employee_wallet_id}`,
  detailPretax: ({ flex_account_id }: { flex_account_id: string }) =>
    `/accounts/pretax/${flex_account_id}`,
});

export const ExternalStoreRoutes = Object.freeze({
  detail: ({
    account_type,
    store_name,
  }: {
    account_type: string;
    store_name: string;
  }) => `/external_store/${account_type}/${store_name}`,
});

export const StoreRoutes = Object.freeze({
  detailPretax: ({ pretax_account }: { pretax_account: string }) =>
    `/explore/pretax_accounts/${pretax_account}`,
  listExplore: () => `/explore`,
  listFeatured: ({
    employee_wallet_id,
    featured_section,
  }: {
    employee_wallet_id: string;
    featured_section: string;
  }) =>
    `/explore/accounts/${employee_wallet_id}/featured/${encodeURIComponent(
      featured_section
    )}`,
  listCategory: ({
    employee_wallet_id,
    category_id,
    alias,
  }: {
    employee_wallet_id: string;
    category_id: string;
    alias?: string;
  }) =>
    `/explore/accounts/${employee_wallet_id}/products/${category_id}${toQueryString(
      { alias }
    )}`,
  search: ({ query }: { query: string }) =>
    `/search${toQueryString({ q: query })}`,
});

export const RecognitionRoutes = Object.freeze({
  detail: ({ recognition_id }: { recognition_id: string }) =>
    `/recognition/${recognition_id}`,
  list: () => `/recognition`,
  presentation: ({ id }: { id: string }) =>
    `/recognition/presentation${toQueryString({ id })}`,
});

export const SettingsRoutes = Object.freeze({
  root: () => `/settings`,
  integrations: () => `/settings/integrations`,
  notifications: () => `/settings/notifications`,
  payment: () => `/settings/payment`,
  profile: () => `/settings/profile`,
  security: () => `/settings/security`,
  pretaxCardList: () => `/settings/pretax/cards`,
  pretaxCardNew: ({ dependent_id }: { dependent_id?: string } = {}) =>
    `/settings/pretax/cards/new${toQueryString({ dependent_id })}`,
  pretaxDependentsDetail: ({ dependent_id }: { dependent_id: string }) =>
    `/settings/pretax/dependents/${dependent_id}`,
  twicCardList: () => `/settings/employer-sponsored/cards`,
  twicCardNew: ({ card_type }: { card_type?: string } = {}) =>
    `/settings/employer-sponsored/cards/new${toQueryString({ card_type })}`,
  twicCardEdit: () => `/settings/employer-sponsored/cards/settings`,
  twicCardSuccess: ({
    stripe_card_id,
    card_type,
  }: {
    stripe_card_id: string;
    card_type?: string;
  }) =>
    `/settings/employer-sponsored/cards/${stripe_card_id}/success${toQueryString(
      { card_type }
    )}`,
  twicCardReplace: ({ stripe_card_id }: { stripe_card_id: string }) =>
    `/settings/employer-sponsored/cards/${stripe_card_id}/replace`,
  bankAccountList: () => `/settings/bank-accounts`,
});

export const WelcomeRoutes = Object.freeze({
  employerSponsored: () => `/welcome/employer-sponsored`,
  pretax: () => `/welcome/pretax`,
  waysToSpend: () => `/welcome/ways-to-spend`,
  finishAccount: () => `/welcome/finish-account`,
  setUpPassword: () => `/welcome/set-up-password`,
});

export const VendorRoutes = Object.freeze({
  search: ({
    lat,
    long,
    name,
    wallet_id,
    activities,
    bounds,
    category_title,
  }: {
    lat?: string;
    long?: string;
    name?: string;
    wallet_id?: string;
    activities?: string;
    bounds?: string;
    category_title?: string;
  }) =>
    `/explore/search${toQueryString({
      lat,
      long,
      name,
      wallet_id,
      activities,
      bounds,
      category_title,
    })}`,
  gymAndFitness: ({
    lat,
    long,
    name,
    wallet_id,
    activities,
    bounds,
  }: {
    lat?: string;
    long?: string;
    name?: string;
    wallet_id?: string;
    activities?: string;
    bounds?: string;
  }) =>
    `/explore/gym-and-fitness${toQueryString({
      lat,
      long,
      name,
      wallet_id,
      activities,
      bounds,
    })}`,
  detail: ({
    vendor_id,
    product_id,
    lat,
    long,
    name,
    ref,
    vendorVenueId,
  }: {
    vendor_id?: string;
    product_id?: string;
    lat?: string;
    long?: string;
    name?: string;
    ref?: string;
    vendorVenueId?: string;
  }) =>
    `/explore/${vendor_id}/${product_id}${toQueryString({
      lat,
      long,
      name,
      ref,
      vendorVenueId,
    })}`,
});

export const SlackRoutes = Object.freeze({
  auth: () => `/slack/auth`,
  install: () => `/slack/install`,
});

export const TransactionRoutes = Object.freeze({
  detail: ({ transaction_id }: { transaction_id: string }) =>
    `/orders/${transaction_id}`,
  detailTwicCard: ({ transaction_id }: { transaction_id: string }) =>
    `/orders/twic-card-transactions/${transaction_id}`,
});

export const SubscriptionRoutes = Object.freeze({
  detail: ({ subscription_id }: { subscription_id: string }) =>
    `/subscriptions/${subscription_id}/manage-membership`,
  manageMembership: ({ subscription_id }: { subscription_id: string }) =>
    `/subscriptions/${subscription_id}/manage-membership`,
  paymentsHistory: ({ subscription_id }: { subscription_id: string }) =>
    `/subscriptions/${subscription_id}/payments-history`,
});

export const HistoryRoutes = Object.freeze({
  subscriptionList: () => `/history/subscriptions`,
  orderList: () => `/history/orders`,
});

export const EnrollRoutes = Object.freeze({
  commuterContribution: ({
    transit_default,
    parking_default,
    plan_year,
  }: {
    transit_default?: number;
    parking_default?: number;
    plan_year?: number;
  }) =>
    `/accounts/pretax/commuter-enroll${toQueryString({
      transit_default,
      parking_default,
      plan_year,
    })}`,
  commuterReview: ({
    transit_personal,
    transit_match,
    transit_waived,
    parking_personal,
    parking_match,
    parking_waived,
    plan_year,
  }: {
    transit_personal?: number;
    transit_match?: number;
    transit_waived?: boolean;
    parking_personal?: number;
    parking_match?: number;
    parking_waived?: boolean;
    plan_year?: number;
  }) =>
    `/accounts/pretax/commuter-enroll-review${toQueryString({
      transit_personal,
      transit_match,
      transit_waived,
      parking_personal,
      parking_match,
      parking_waived,
      plan_year,
    })}`,
  commuterConfirmed: () => '/accounts/pretax/commuter-enroll-confirmed',
});
